import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Stethoscope, Plus, Trash2, Info } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AlertFilterSection({ darkMode, practice, updateDisplaySettings }) {
  return (
    <div className="glass-panel rounded-2xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-heading font-bold text-slate-800">Alert Filters</h2>
        <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">Reduce notification noise</span>
      </div>
      
      {/* Master Toggle */}
      <div className="flex items-center gap-3 mb-4 pb-4 border-b border-slate-200">
        <button
          onClick={async () => {
            const result = await updateDisplaySettings({ 
              alert_filtering_enabled: !(practice?.alert_filtering_enabled !== false)
            });
            if (result.success) {
              toast.success((practice?.alert_filtering_enabled !== false) 
                ? 'Alert filtering disabled' 
                : 'Alert filtering enabled');
            } else {
              toast.error(result.error);
            }
          }}
          data-testid="toggle-alert-filtering"
          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full transition-colors ${
            (practice?.alert_filtering_enabled !== false) ? 'bg-dp-600' : 'bg-slate-300'
          }`}
        >
          <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform mt-0.5 ${
            (practice?.alert_filtering_enabled !== false) ? 'translate-x-5' : 'translate-x-0.5'
          }`} />
        </button>
        <div className="flex flex-col">
          <span className="text-slate-700 text-sm font-medium">Enable Smart Filtering</span>
          <span className="text-xs text-slate-500">Master switch for all role-based filters below</span>
        </div>
      </div>
      
      {/* Individual Filter Toggles */}
      <div className="space-y-3">
        <FilterToggle
          testId="toggle-filter-same-computer"
          label="Same Computer Filter"
          description="Don't show alerts on the computer that created them"
          enabled={practice?.filter_same_computer !== false}
          onChange={() => updateDisplaySettings({ filter_same_computer: !(practice?.filter_same_computer !== false) })}
        />
        <FilterToggle
          testId="toggle-filter-hygienist-busy"
          label="Hygienist Busy Filter"
          description='Don&apos;t show "Help Needed" to hygienists with seated patients'
          enabled={practice?.filter_hygienist_busy !== false}
          onChange={() => updateDisplaySettings({ filter_hygienist_busy: !(practice?.filter_hygienist_busy !== false) })}
        />
        <FilterToggle
          testId="toggle-filter-doctor-specific"
          label="Doctor Specific Filter"
          description='"Dr. Needed" alerts only go to the specific doctor requested'
          enabled={practice?.filter_doctor_specific !== false}
          onChange={() => updateDisplaySettings({ filter_doctor_specific: !(practice?.filter_doctor_specific !== false) })}
        />
        <FilterToggle
          testId="toggle-filter-hygienist-operatory"
          label="Hygienist Operatory Filter"
          description="Hygienists only see alerts for their assigned operatories"
          enabled={practice?.filter_hygienist_operatory !== false}
          onChange={() => updateDisplaySettings({ filter_hygienist_operatory: !(practice?.filter_hygienist_operatory !== false) })}
        />
      </div>
      
      {/* Dr. in Room Tracking Section */}
      <div className={`mt-6 pt-4 ${darkMode ? 'border-t border-slate-700' : 'border-t border-slate-200'}`}>
        <div className={`flex items-center gap-3 p-4 rounded-lg border ${
          darkMode ? 'bg-indigo-900/30 border-indigo-800' : 'bg-indigo-50 border-indigo-200'
        }`}>
          <button
            onClick={async () => {
              const result = await updateDisplaySettings({ 
                dr_in_room_enabled: !(practice?.dr_in_room_enabled || false)
              });
              if (result.success) {
                toast.success(practice?.dr_in_room_enabled 
                  ? 'Dr. in Room tracking disabled' 
                  : 'Dr. in Room tracking enabled');
              }
            }}
            data-testid="toggle-dr-in-room"
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full transition-colors ${
              (practice?.dr_in_room_enabled) ? 'bg-indigo-600' : 'bg-slate-300'
            }`}
          >
            <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform mt-0.5 ${
              (practice?.dr_in_room_enabled) ? 'translate-x-5' : 'translate-x-0.5'
            }`} />
          </button>
          <div className="flex-1">
            <span className={`text-sm font-semibold flex items-center gap-2 ${darkMode ? 'text-indigo-300' : 'text-indigo-800'}`}>
              <Stethoscope className="w-4 h-4" />
              Dr. in Room Tracking
            </span>
            <p className={`text-xs mt-1 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
              Adds a "Dr. in Room" status to patient flow (for doctor operatories) and Dr. Needed sequence.
              Tracks exam/treatment time for reporting metrics.
            </p>
          </div>
        </div>
      </div>
      
      {/* Custom Filters Section */}
      <CustomFiltersSubsection darkMode={darkMode} practice={practice} />
    </div>
  );
}

function FilterToggle({ testId, label, description, enabled, onChange }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
      <button
        onClick={async () => {
          const result = await onChange();
          if (result?.success) toast.success('Filter updated');
        }}
        data-testid={testId}
        className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full transition-colors ${
          enabled ? 'bg-emerald-500' : 'bg-slate-300'
        }`}
      >
        <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition-transform mt-0.5 ${
          enabled ? 'translate-x-4' : 'translate-x-0.5'
        }`} />
      </button>
      <div className="flex-1">
        <span className="text-slate-700 text-sm font-medium">{label}</span>
        <p className="text-xs text-slate-500">{description}</p>
      </div>
    </div>
  );
}

function CustomFiltersSubsection({ darkMode, practice }) {
  return (
    <div className="mt-6 pt-4 border-t border-slate-200">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-700">Custom Filters</h3>
        <Dialog>
          <Button size="sm" variant="outline" className="h-7 text-xs" asChild>
            <span><Plus className="w-3 h-3 mr-1" />Request New Filter</span>
          </Button>
          <DialogContent className="glass-panel max-w-md">
            <DialogHeader>
              <DialogTitle>Request a Custom Filter</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-sm text-slate-600">
                Describe the filter behavior you need and we'll help you set it up:
              </p>
              <div>
                <Label>Filter Name</Label>
                <input className="w-full mt-1 p-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-dp-500" placeholder="e.g., Front Desk Quiet Mode" id="custom-filter-name" />
              </div>
              <div>
                <Label>Description</Label>
                <textarea 
                  className="w-full mt-1 p-2 border rounded-lg text-sm min-h-[100px] focus:outline-none focus:ring-2 focus:ring-dp-500"
                  placeholder="Describe what alerts should be hidden and for whom."
                  id="custom-filter-description"
                />
              </div>
              <div className="bg-amber-50 p-3 rounded-lg">
                <p className="text-xs text-amber-700">
                  <strong>Available filter types:</strong><br/>
                  - Exclude roles from certain alert types<br/>
                  - Limit alerts to assigned operatories<br/>
                  - Time-based filters (coming soon)
                </p>
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button 
                onClick={async () => {
                  const name = document.getElementById('custom-filter-name')?.value;
                  const description = document.getElementById('custom-filter-description')?.value;
                  if (!name) { toast.error('Please enter a filter name'); return; }
                  try {
                    await axios.post(`${API}/practice/custom-filters`, {
                      name, description,
                      rule_type: 'exclude_role_from_alert_type',
                      conditions: { roles: [], alert_types: [] }
                    });
                    toast.success('Filter request submitted! Configure it below.');
                    window.location.reload();
                  } catch {
                    toast.error('Failed to create filter');
                  }
                }}
                className="bg-dp-600 hover:bg-dp-700"
              >
                Create Filter
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      
      {practice?.custom_filters?.length > 0 ? (
        <div className="space-y-2">
          {practice.custom_filters.map((filter) => (
            <div key={filter.id} className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
              <button
                onClick={async () => {
                  try {
                    await axios.put(`${API}/practice/custom-filters/${filter.id}`, { enabled: !filter.enabled });
                    toast.success('Filter updated');
                    window.location.reload();
                  } catch { toast.error('Failed to update filter'); }
                }}
                className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full transition-colors ${
                  filter.enabled ? 'bg-purple-500' : 'bg-slate-300'
                }`}
              >
                <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition-transform mt-0.5 ${
                  filter.enabled ? 'translate-x-4' : 'translate-x-0.5'
                }`} />
              </button>
              <div className="flex-1">
                <span className="text-slate-700 text-sm font-medium">{filter.name}</span>
                {filter.description && <p className="text-xs text-slate-500">{filter.description}</p>}
              </div>
              <button
                onClick={async () => {
                  try {
                    await axios.delete(`${API}/practice/custom-filters/${filter.id}`);
                    toast.success('Filter deleted');
                    window.location.reload();
                  } catch { toast.error('Failed to delete filter'); }
                }}
                className="p-1 text-slate-400 hover:text-red-500 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-500 italic">No custom filters configured</p>
      )}
    </div>
  );
}
